import React, { useState, useEffect } from "react";
import "./homepage.css";
import sandy from "../assets/SANDY-RANDHAWA.png";
import { useLocation, useNavigate, Link } from "react-router-dom";
import base_url from "../api";

const Homepage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });

  // Fetch user data if logged in
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await fetch(`${base_url}/me`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.user) {
              setUser(data.user);
              // Pre-fill form with user data
              setFormData({
                name: `${data.user.firstName} ${data.user.lastName}`,
                email: data.user.email,
                phone: data.user.phone || "",
                message: "",
              });
            }
          } else {
            // Token invalid, clear user
            setUser(null);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser(null);
        }
      } else {
        // No token, user is not logged in
        setUser(null);
      }
    };

    fetchUserData();
  }, []);

  // Handle hash navigation when component mounts or hash changes
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1); // Remove the #
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerHeight = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [location.hash]);

  // Scroll to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      const headerHeight = 80;
      const elementPosition = contactSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Only allow changing message if user is logged in, otherwise allow all fields
    if (user && name !== "message") {
      return; // Don't allow editing name, email, phone if user is logged in
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!user) {
      setSubmitMessage({
        type: "error",
        text: "Please login first to send a message.",
      });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: "", text: "" });

    try {
      const response = await fetch(`${base_url}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage({
          type: "success",
          text: data.message || "Your message has been sent successfully!",
        });
        // Clear message field only
        setFormData((prev) => ({
          ...prev,
          message: "",
        }));
        // Clear message after 5 seconds
        setTimeout(() => {
          setSubmitMessage({ type: "", text: "" });
        }, 5000);
      } else {
        setSubmitMessage({
          type: "error",
          text: data.message || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setSubmitMessage({
        type: "error",
        text: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      title: "Divorce Law",
      desc: "From petition to final decree, we guide you through every step with clarity and confidence.",
      icon: "⚖️",
    },
    {
      title: "Financial Settlements",
      desc: "Ensuring fair division of assets, pensions, and financial support.",
      icon: "❤️",
    },
    {
      title: "Children & Parenting Arrangements",
      desc: "Focused on the best interests of your children — including residence, contact, and safety concerns.",
      icon: "👨‍👩‍👧",
    },
    {
      title: "Pre-Nuptial & Post-Nuptial Agreements",
      desc: "Protect your future with legally sound agreements tailored to your needs.",
      icon: "📄",
    },
    {
      title: "Mediation & Alternative Dispute Resolution",
      desc: "Helping you find sensible solutions outside of court.",
      icon: "💬",
    },
  ];
  const steps = [
    {
      step: "1",
      title: "Initial Consultation",
      desc: "We listen to you, understand your goals, and answer your questions.",
    },
    {
      step: "2",
      title: "Strategic Planning",
      desc: "We recommend tailored approaches — negotiation, mediation, or court representation.",
    },
    {
      step: "3",
      title: "Clear Communication",
      desc: "You receive regular updates and honest guidance at every step.",
    },
    {
      step: "4",
      title: "Focused Resolution",
      desc: "Our aim is to achieve fair, practical outcomes with minimum stress.",
    },
  ];

  const testimonials = [
    {
      name: "E.T.",
      text: "Sandy helped me through one of the hardest times of my life with professionalism and compassion. I always felt supported.",
    },
    {
      name: "J.M.",
      text: "I couldn't have asked for a better solicitor. Sandy communicated every step clearly and fought for a fair settlement.",
    },
    {
      name: "S.L.",
      text: "The guidance and support I received was exceptional. Sandy made a difficult process much more manageable.",
    },
  ];
  const guides = [
    {
      title: "Understanding Divorce Law in England",
      desc: "A comprehensive guide to the divorce process, from initial petition to final decree.",
    },
    {
      title: "Child Arrangement Orders: What You Need to Know",
      desc: "Learn about custody arrangements, contact schedules, and protecting your children's interests.",
    },
    {
      title: "Protecting Your Financial Interests",
      desc: "Essential advice on asset division, pensions, and securing your financial future.",
    },
    {
      title: "Preparing for Mediation: A Checklist",
      desc: "Tips and strategies to help you approach mediation with confidence and clarity.",
    },
  ];
  const [open, setOpen] = useState(false);

  return (
    <>
      <section id="home" className="hero">
        <div className="hero-container">
          {/* Left Content */}
          <div className="hero-content">
            <h1>Expert Divorce & Family Law Solicitor in England</h1>

            <p className="subtitle">
              Compassionate, clear-sighted legal support to protect what matters most.
            </p>

            <div className="hero-actions">
              <button className="primary-btn" onClick={scrollToContact}>
                Book a Free Consultation
                <i className="fa-solid fa-arrow-right"></i>
              </button>

              <button className="secondary-btn">
                <i className="fa-solid fa-phone"></i>
                01900 123456
              </button>
            </div>
            <hr></hr>
            <p className="description">
              With over 15 years of experience in family and divorce law, Sandy Randhawa
              provides trusted legal advice with empathy and professionalism. Whether
              you're navigating divorce, financial settlements, or child arrangements,
              you're in capable hands.
            </p>
          </div>

          {/* Right Image */}
          <div className="hero-image">
            {/* <img src={heroImg} alt="Legal consultation meeting" /> */}
          </div>
        </div>
      </section>
        <section id="about" className="sandy-section">
          {/* Background Text */}
          <div className="sandy-bg-text">SANDY</div>
          <div className="path-overlay" />


          <div className="sandy-container path-container">
            {/* Left Image */}
            <div className="sandy-image">
              <img src={sandy} alt="Sandy Randhawa" />
            </div>

            {/* Right Content */}
            <div className="sandy-content">
              <span className="sandy-tag">Meet Sandy Randhawa</span>

              <h2>
                An Advocate for Fair Outcomes <br />
                & Supportive Guidance
              </h2>

              <p>Sandy Randhawa is an experienced family and matrimonial solicitor who advises on all aspects of divorce, separation and nullity, including the resolution of financial, property and pension issues. Sandy also deals with cases involving an international element, jurisdiction disputes and issues relating to children, injunctions relevant to domestic abuse, and advises on cohabitation issues and pre-marital agreements.</p>

              <p>She has represented a diverse range of clients both in England and internationally. These clients include footballers, senior company directors, pilots, teachers and police officers.</p>
              {open && (
                <div className="sandy-expand animate-expand">
                  <p>Sandy has successfully made a name for herself in dealing with complicated financial disputes involving high net worth assets, disputes regarding ownership of property, third party interests and trusts claims. Her understanding of important cultural practices in certain communities has assisted in carefully bringing complicated ownership claims to the court’s attention to ensure that she secures excellent results for her clients.</p>

                  <p>As a Senior Solicitor with years of experience, Sandy prides herself on treating each case individually and reaching a conclusion as sensitively and speedily as possible. She recognises that relationship breakdown is a very difficult and emotionally challenging time for clients and therefore provides as much support and understanding as possible. Sandys’ philosophy is to deal with matters in a conciliatory manner, whilst adopting a no nonsense approach to secure the best outcome for her clients.</p>

                  <p>As well as being a member of Resolution (formerly known as SFLA), Sandy is an accredited member of the Law Society’s Family Law Panel and a member of the Middlesex Law Society, having previously been the Lecture Secretary for the Buckinghamshire, Berkshire and Oxfordshire Law Society. She is also on the management committee for Slough Contact Centre.</p>

                  <p>Having appeared on television and radio, Sandy is regularly invited to deliver presentations to various organisations. She has worked closely with local courts and Judges in presenting topical issues to practitioners and Judges. She is currently involved in hosting workshops for other professionals to raise awareness of the issues facing family and matrimonial clients. She also undertakes pro-bono work in the local community.</p>
                </div>
              )}
              <p>Sandy is fluent in Punjabi, Hindi and Urdu.</p>

              <b> What sets Sandy apart:</b>


              <ul className="sandy-points">
                <li>Personalised, client-focused approach</li>
                <li>Clear and realistic guidance</li>
                <li>Strong negotiator in and out of court</li>
                <li>Committed to resolving disputes constructively</li>
              </ul>

              <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                
                <Link to="/about" className="sandy-btn" style={{ textDecoration: "none", display: "inline-block" }}>
                Learn More About Sandy →
                </Link>
              </div>
            </div>
          </div>
        </section>
      <section id="services" className="expertise-section">
        {/* Background text */}
        <div className="expertise-bg-text">EXPERTISE</div>

        <div className="expertise-header">
          <h2>Our Legal Expertise</h2>
          <p>
            We offer specialist support in all areas of family law, including:
          </p>
        </div>

        <div className="expertise-grid">
          {services.map((item, index) => (
            <div className="expertise-card" key={index}>
              <div className="expertise-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="expertise-actions">
          <Link to="/services" className="btn-outline" style={{ textDecoration: "none", display: "inline-block" }}>
            View All Services →
          </Link>
        </div>
      </section>
      <section id="how-we-work" className="path-section">
        {/* Overlay */}
        <div className="path-overlay" />

        <div className="path-container width-900">
          {/* Header */}
          <div className="path-header">
            <h2>Your Path to Resolution</h2>
            <p>
              We believe in transparent legal support. Here's how we work with you:
            </p>
          </div>

          {/* Timeline */}
          <div className="path-timeline">
            {steps.map((item, index) => (
              <div className="path-item" key={index}>
                <div className="path-circle">
                  {item.step}
                </div>

                <div className="path-content">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="path-action" style={{ display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap" }}>
            <button className="path-btn" onClick={scrollToContact}>
              Book a Consultation →
            </button>
            <Link to="/how-we-work" className="path-btn" style={{ textDecoration: "none", display: "inline-block" }}>
              Learn More About Our Process →
            </Link>
          </div>
        </div>
      </section>
      <section id="testimonials" className="testimonials-section  path-container">
        <div className="path-overlay" />

        <div className="testimonials-header">
          <h2>What Our Clients Say</h2>
          <p>
            Don't just take our word for it — hear from those we've helped
          </p>
        </div>

        <div className="testimonials-grid">
          {testimonials.map((item, index) => (
            <div className="testimonial-card" key={index}>
              <div className="stars">★★★★★</div>

              <p className="testimonial-text">
                “{item.text}”
              </p>

              <span className="testimonial-name">
                — {item.name}
              </span>
            </div>
          ))}
        </div>

        <div className="testimonial-actions">
          <Link to="/testimonials" className="btn-outline" style={{ textDecoration: "none", display: "inline-block" }}>
            Read All Testimonials →
          </Link>
        </div>
      </section>
      <section id="resources" className="guides-section">
        <div className="guides-header">
          <h2>Legal Guides & Support</h2>
          <p>
            Helpful articles written by Sandy to help you understand your rights
          </p>
        </div>

        <div className="guides-grid">
          {guides.map((item, index) => (
            <div className="guide-card" key={index}>
              <div className="guide-icon">📘</div>

              <div className="guide-content">
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>

              <div className="guide-arrow">→</div>
            </div>
          ))}
        </div>

        <div className="guides-action">
          <Link to="/resources" className="btn-outline" style={{ textDecoration: "none", display: "inline-block" }}>
            Explore All Legal Guides →
          </Link>
        </div>
      </section>
      <section id="contact" className="contact-section">
        {/* Background Overlay */}
        <div className="contact-overlay" />

        {/* Content */}
        <div className="contact-content">
          {/* Header */}
          <div className="contact-header">
            <h2>Get in Touch</h2>
            <p>
              Whether you're ready to start your case or just need advice, we're here to help.
            </p>
          </div>

          <div className="contact-wrapper">
            {/* Left Info */}
            <div className="contact-info">
              <div className="info-card">
                <div className="info-icon">📞</div>
                <div>
                  <h4>Phone</h4>
                  <p className="info-link">01900 123456</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">✉️</div>
                <div>
                  <h4>Email</h4>
                  <p className="info-link">info@sandyrandhawa.com</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">📍</div>
                <div>
                  <h4>Office</h4>
                  <p>
                    123 Legal Street <br />
                    Manchester, England <br />
                    M1 1AA
                  </p>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={!user ? "Please login first" : formData.name}
                    placeholder={!user ? "Please login first" : "Your full name"}
                    disabled={true}
                    required
                    style={{
                      backgroundColor: "#e5e7eb",
                      cursor: "not-allowed",
                      color: !user ? "#9ca3af" : "#000",
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={!user ? "Please login first" : formData.email}
                    placeholder={!user ? "Please login first" : "your@email.com"}
                    disabled={true}
                    required
                    style={{
                      backgroundColor: "#e5e7eb",
                      cursor: "not-allowed",
                      color: !user ? "#9ca3af" : "#000",
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={!user ? "Please login first" : formData.phone}
                  placeholder={!user ? "Please login first" : "Your phone number"}
                  disabled={true}
                  style={{
                    backgroundColor: "#e5e7eb",
                    cursor: "not-allowed",
                    color: !user ? "#9ca3af" : "#000",
                  }}
                />
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  name="message"
                  value={!user ? "Please login first" : formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder={!user ? "Please login first" : "Tell us about your situation and how we can help..."}
                  disabled={!user}
                  required
                  style={{
                    backgroundColor: !user ? "#e5e7eb" : "#f4f4f4",
                    cursor: !user ? "not-allowed" : "text",
                    color: !user ? "#9ca3af" : "#000",
                  }}
                />
              </div>

              {submitMessage.text && (
                <div
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    backgroundColor:
                      submitMessage.type === "success" ? "#d1fae5" : "#fee2e2",
                    color: submitMessage.type === "success" ? "#065f46" : "#991b1b",
                    fontSize: "14px",
                  }}
                >
                  {submitMessage.text}
                </div>
              )}

              <button
                type="submit"
                className="contact-btn"
                disabled={isSubmitting || !user}
                style={{
                  opacity: !user ? 0.6 : 1,
                  cursor: !user ? "not-allowed" : "pointer",
                }}
              >
                {!user ? "Please Login First" : isSubmitting ? "Sending..." : "Send Message"}
              </button>

              <p className="form-note">
                All consultations are confidential. We typically respond within 24 hours.
              </p>
            </form>
              {/* <div style={{ textAlign: "center", marginTop: "30px" }}>
                <Link to="/contact" className="btn-outline" style={{ textDecoration: "none", display: "inline-block" }}>
                  Visit Full Contact Page →
                </Link>
              </div> */}
          </div>
        </div>
      </section>
    </>
  );
};

export default Homepage;
